import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { mockColorRules } from './color_rules_mock_data';
import { apiCacheService } from './ApiCacheService';
import { SMART_INVALIDATION_RULES } from '@/config/cacheConfig';
import { redirectExternal } from '@/utils/shopifyBridge';
// Circular (atlasIframeService imports getIdentityToken from here). This is
// only safe while nothing calls into this module during webpack's evaluation
// of it — and that is not hypothetical: '@/router' used to be imported
// eagerly here, its eager view imports constructed a ReportModel at module
// scope, and the resulting getIdentityToken() -> setIdentityToken() call
// mid-cycle crashed every returning embedded session on the not-yet-assigned
// binding below ("Cannot read properties of undefined (reading 'b')",
// AS-10032). The router is therefore lazy-imported inside the 401
// interceptor. Do not reintroduce a top-level '@/router' import here, and
// keep module-evaluation-time work out of this file's callers.
import { getAttachedAtlasIframeService } from '@/services/atlasIframeService';
// Leaf module — imports nothing back, so it is safe above the cycle note.
import { createTokenRefresher } from '@/services/tokenFreshness';

// src/types/global.d.ts
declare global {
  interface Window {
    SHOP_NAME?: string;
  }
}

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * AS-11619: a 401 on this request is the caller's to drop. The interceptor
     * neither refreshes, renews, evicts nor navigates for it — fire-and-forget
     * tracking must never drive the session.
     */
    noAuthRetry?: boolean;
  }
}

const host = process.env.VUE_APP_BACKEND_API_URL;

// ---------------------------------------------------------------------------
// AS-11619: one eviction per document.
//
// A definitive auth failure (refresh token rejected, embedded renewal failed,
// refresh failed right after a reload for the same reason) used to clear the
// bearer token and push `/`, and nothing else: `isLogined` stayed true, Home
// re-mounted and re-fired its requests into the dead session, the landing
// redirect moved the route off `/`, and the next 401 pushed `/` again — about
// 1,000 requests a minute from one tab. The latch below makes the first
// definitive failure the last one that does anything: it notifies App.vue,
// which leaves the logged-in state, and until a new login every request that
// needs an identity is rejected here instead of on the wire.
// ---------------------------------------------------------------------------
export type SessionEvictedListener = (reason: string) => void;

let sessionEvicted = false;
const sessionEvictedListeners = new Set<SessionEvictedListener>();

// What a signed-out document may still send: the ways back in and the public
// reads. Matched on whole path segments, so `/tokens_report` is not `/token`.
const SIGNED_OUT_ALLOWED_PREFIXES = ['/token', '/shopify', '/hades', '/published_dashboards', '/files'];

// A refresh failure reloads the page at most once per window per tab. A second
// failure inside the window means the reload did not help: evict instead.
const RELOAD_STAMP_KEY = 'warbler_auth_reload_at';
const RELOAD_GUARD_MS = 30 * 1000;

export function isSessionEvicted(): boolean {
  return sessionEvicted;
}

/** Subscribe to the one eviction per document. Returns the unsubscribe. */
export function onSessionEvicted(listener: SessionEvictedListener): () => void {
  sessionEvictedListeners.add(listener);
  return () => {
    sessionEvictedListeners.delete(listener);
  };
}

function notifySessionEvicted(reason: string) {
  sessionEvictedListeners.forEach((listener) => {
    try {
      listener(reason);
    } catch (e) {
      // A listener must stop neither the eviction nor the listeners after it.
    }
  });
}

/**
 * Clear the identity and, the first time in this document, tell the app and
 * go home. Later calls only re-clear: the app is already in its login state.
 * Listeners run BEFORE the navigation so a handler still sees the route the
 * session died on (App.vue keeps it as `redirect_uri` for the re-login).
 */
async function evictSession(reason: string, pushHome: () => Promise<void>): Promise<void> {
  removeIdentityToken();
  if (sessionEvicted) {
    return;
  }
  sessionEvicted = true;
  notifySessionEvicted(reason);
  await pushHome();
}

function isAllowedWhenSignedOut(url: string): boolean {
  // Only the path is sent: drop the query and the fragment first.
  const path = String(url || '').split(/[?#]/)[0];
  // The browser resolves dot segments (`..`, `%2e%2e`, `.%2e`) and treats `\`
  // as `/` before sending, so `/files/../dashboards/1` would go out as
  // `/dashboards/1`. Nothing in warbler builds such a URL; refuse them rather
  // than let a prefix match stand in for the path that actually leaves.
  if (/\\|\.\.|%2e/i.test(path)) {
    return false;
  }
  return SIGNED_OUT_ALLOWED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

/**
 * Should this request be rejected locally? `identity` is the token the caller
 * is about to send. A non-empty one while evicted can only be a NEW login —
 * every eviction path clears the identity first — and not every login goes
 * through `setIdentityToken` (the email/password form, demo and test logins
 * write `localStorage.token` directly), so the presence of a token is what
 * ends the eviction here.
 */
function isBlockedWhileSignedOut(url: string, identity: string): boolean {
  if (!sessionEvicted) {
    return false;
  }
  if (identity) {
    sessionEvicted = false;
    return false;
  }
  return !isAllowedWhenSignedOut(url);
}

/**
 * The local stand-in for the 401 a signed-out request would have earned: the
 * shape callers already handle (`response.status`, a string `response.data`),
 * plus `code` so the short-circuit is recognisable.
 */
function sessionEvictedError(url: string): Error {
  const error: any = new Error(`Session evicted — ${url} not sent`);
  error.code = 'SESSION_EVICTED';
  error.config = { url };
  error.response = {
    status: 401,
    data: JSON.stringify({ code: 'SESSION_EVICTED', message: 'Your session has expired. Please sign in again.' }),
  };
  return error;
}

function reloadedRecently(): boolean {
  try {
    const stamp = Number(sessionStorage.getItem(RELOAD_STAMP_KEY) || 0);
    return stamp > 0 && Date.now() - stamp < RELOAD_GUARD_MS;
  } catch {
    return false;
  }
}

function stampReload() {
  try {
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
  } catch {
    // Storage blocked: the guard degrades to today's unguarded reload.
  }
}

/**
 * The recovery of last resort: a fresh boot can re-run the session exchange.
 * At most once per guard window per tab — a second failure inside it means
 * the reload did not help, and reloading again would only start the next lap,
 * so the session is evicted instead. The latch is armed and the app told
 * BEFORE reloading: stragglers from this burst must not each start a renewal
 * of their own while the reload is on its way, and a reload that a
 * `beforeunload` prompt cancels must leave a login screen behind, not a
 * logged-in page whose every request is rejected locally. When the reload
 * proceeds, the fresh document starts un-evicted. No `pushHome`: the page is
 * leaving.
 */
async function reloadOnceOrEvict(reason: string, pushHome: () => Promise<void>): Promise<void> {
  if (reloadedRecently()) {
    await evictSession(reason, pushHome);
    return;
  }
  removeIdentityToken();
  if (sessionEvicted) {
    return;
  }
  sessionEvicted = true;
  notifySessionEvicted(`${reason}_reloading`);
  stampReload();
  window.location.reload();
}

// build http header
function buildHeader(): { [key: string]: string } {
  return {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': '*',
    'Accept': 'application/json',
  };
}

function getCurrentUrl() {
  let currentUrl: string = document.location.href;
  if (window.location != window.parent.location) {
    currentUrl = document.referrer;
  }
  return currentUrl;
}

function appendMockMode(url: string): string {
  if (process.env.VUE_APP_API_MODE === 'mock') {
    if (url.includes('?')) {
      return `${url}&api_mode=mock`;
    }
    return `${url}?api_mode=mock`;
  }
  return url;
}

function isEmbbedApp() {
  try {
    return (window as any).shopify?.environment?.embedded ??
      getCurrentUrl().includes('admin.shopify.com');
  } catch {
    return getCurrentUrl().includes('admin.shopify.com');
  }
}

// function get Identity Token
export function verifyIdentityToken(): boolean {
  if (isEmbbedApp()) {
    // Use localStorage instead of window.SHOP_NAME (which is lost on page refresh)
    // @ts-ignore: Unreachable code error
    const storeName = window.SHOP_NAME || localStorage.getItem('shopify_store');
    let tokenOptions: any = localStorage.getItem('token_options');
    if (tokenOptions) {

      tokenOptions = JSON.parse(tokenOptions);
      if (storeName && Object.keys(tokenOptions).includes(storeName)) {
        setIdentityToken(
          tokenOptions[storeName].token,
          tokenOptions[storeName].shop_name,
        );
        return true;
      }
    }
    return false;
  }
  return true;
}

export function getIdentityToken() {
  // @ts-ignore: Unreachable code error
  if (isEmbbedApp() || window.SHOP_NAME) {
    const storeName = window.SHOP_NAME || localStorage.getItem('shopify_store');
    let tokenOptions: any = localStorage.getItem('token_options');
    if (tokenOptions) {
      tokenOptions = JSON.parse(tokenOptions);
      if (storeName && Object.keys(tokenOptions).includes(storeName)) {
        setIdentityToken(
          tokenOptions[storeName].token,
          tokenOptions[storeName].shop_name,
        );
      }
    }
  }
  return localStorage.getItem('token') || '';
}

export function setIdentityToken(token: string, storeName: string) {
  const currentStore = localStorage.getItem('shopify_store');

  // AS-11619: a new identity ends the eviction — requests may go out again.
  sessionEvicted = false;

  localStorage.setItem('token', token);
  localStorage.setItem('shopify_store', storeName);

  // @ts-ignore: Unreachable code error
  window.SHOP_NAME = storeName;

  let tokenOptions: any = localStorage.getItem('token_options');
  if (tokenOptions) {
    tokenOptions = JSON.parse(tokenOptions);
  } else {
    tokenOptions = {};
  }
  tokenOptions[storeName] = { token, shop_name: storeName };
  localStorage.setItem('token_options', JSON.stringify(tokenOptions));

  // Clear cache when switching to a different store
  if (currentStore && currentStore !== storeName) {
    apiCacheService.clearStoreCache(currentStore);
  }

  // AS-10032: push the fresh token to an embedded Atlas iframe. Warbler is the
  // source of truth for the identity token, but it only used to hand one over
  // at iframe READY / REQUEST_AUTH — so a background refresh left Atlas using
  // the old token until something remounted the iframe, surfacing as sporadic
  // 401s in long sessions.
  //
  // Must go through the ATTACHED instance: AtlasEmbed builds its own service
  // via createAtlasIframeService(), so the exported singleton is never behind a
  // live iframe and pushing to it would silently do nothing. Null when Atlas is
  // not mounted, which also keeps the common case free of console noise.
  // Pass the token explicitly — letting the service re-derive it would call
  // getIdentityToken(), which calls back into this function, recursing until
  // the stack blows.
  getAttachedAtlasIframeService()?.sendAuthToken(token);
}

export function removeIdentityToken() {
  const storeName = localStorage.getItem('shopify_store') || '';
  let tokenOptions: any = localStorage.getItem('token_options');
  if (tokenOptions) {
    tokenOptions = JSON.parse(tokenOptions);
    if (Object.keys(tokenOptions).includes(storeName)) {
      delete tokenOptions[storeName];
    }
  } else {
    tokenOptions = {};
  }
  localStorage.setItem('token_options', JSON.stringify(tokenOptions));
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
}

// Server-side counterpart to removeIdentityToken: best-effort revoke of the
// remember-me session before the local tokens are cleared. Errors are swallowed
// so logout always proceeds (a stateless, non-remember-me token revokes nothing).
export async function revokeIdentitySession() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    return;
  }
  try {
    await post('/token/logout', { refresh_token: refreshToken });
  } catch (e) {
    // best-effort: proceed with local logout regardless
  }
}

// One refresh in flight per tab, shared by the 401 interceptor and by
// `getFreshIdentityToken` (which the Atlas iframe reaches through
// `sendAuthToken`). Without the sharing, a burst of 401s — or a burst of
// REQUEST_AUTH messages from the iframe — fires one `/token/refresh` per
// failing request, each rotating the refresh token, and all but the last
// result is discarded.
const tokenRefresher = createTokenRefresher({
  getToken: () => getIdentityToken(),
  getRefreshToken: () => localStorage.getItem('refresh_token'),
  requestRefresh: async (refreshToken: string) => {
    const response: any = await post('/token/refresh', {
      refresh_token: refreshToken,
    });
    // A 200 carrying a malformed body would otherwise surface as a TypeError
    // on `response.data.token`. The failure degrades safely either way, but an
    // explicit error keeps the reason readable in logs.
    if (!response?.data?.token || !response?.data?.refresh_token) {
      throw new Error('Malformed /token/refresh response');
    }
    return {
      token: response.data.token,
      refreshToken: response.data.refresh_token,
    };
  },
  onRefreshed: ({ token, refreshToken }) => {
    // Store the rotated refresh token FIRST. setIdentityToken notifies the
    // attached Atlas iframe synchronously, and anything that reacted by
    // refreshing would otherwise still read the refresh token the backend has
    // just rotated out.
    localStorage.setItem('refresh_token', refreshToken);
    setIdentityToken(token, localStorage.getItem('shopify_store') || '');
  },
  renewSession: () => renewShopifySession(),
});

/**
 * AS-11299: renew an embedded OWNER session (no refresh token) by re-running
 * the `/shopify/session` exchange. Lazy import: shopifySessionAuth imports
 * `post` and `setIdentityToken` from this module, so a top-level import would
 * add a second module-init cycle like the one described at the top of this
 * file. Resolves null whenever renewal is impossible (flag off, standalone tab,
 * App Bridge cannot mint, exchange refused).
 */
async function renewShopifySession(): Promise<string | null> {
  const { renewShopifySessionOnce } = await import('@/services/shopifySessionAuth');
  return renewShopifySessionOnce();
}

/**
 * Refresh the access token, coalescing concurrent callers into one request.
 * Rejects when there is no refresh token to present.
 */
export function refreshAccessTokenOnce(): Promise<string> {
  return tokenRefresher.refreshOnce();
}

/**
 * Resolve a token that is safe to send right now.
 *
 * Refreshes first only when the current token is genuinely about to expire:
 * with the refresh token when one exists, otherwise (embedded OWNER, AS-11299)
 * by re-running the `/shopify/session` exchange. A token with no `exp` claim is
 * returned untouched and never triggers either — see `tokenFreshness.ts`.
 */
export function getFreshIdentityToken(): Promise<string> {
  return tokenRefresher.getFreshToken();
}

export let ax = axios.create({
  baseURL: host,
  headers: buildHeader(),
  timeout: 300000,
  responseType: 'json',
  transformRequest: [
    (data) => {
      return data;
    },
  ],
  transformResponse: [
    (data) => {
      if (data) {
        return data;
      } else {
        return {};
      }
    },
  ],
});

// Helper function to get current store ID for caching
function getCurrentStoreId(): string {
  return localStorage.getItem('shopify_store') || 'default';
}

// Helper function to get base URL pattern from full URL
function getBaseUrlPattern(url: string): string {
  // Remove query parameters and trailing slashes
  const baseUrl = url.split('?')[0].replace(/\/$/, '');
  
  // Extract the base pattern (e.g., /segments/123 -> /segments)
  const segments = baseUrl.split('/');
  if (segments.length > 2) {
    // Return base pattern like /segments, /schedule-reports
    return `/${segments[1]}`;
  }
  return baseUrl;
}

// Smart cache invalidation after successful mutations
function handleCacheInvalidation(url: string, method: string, statusCode: number): void {
  // Only invalidate on successful mutations
  if (method.toUpperCase() === 'GET' || statusCode < 200 || statusCode >= 300) {
    return;
  }

  const storeId = getCurrentStoreId();
  const basePattern = getBaseUrlPattern(url);
  
  // Check if this URL has smart invalidation rules
  const invalidationPatterns = SMART_INVALIDATION_RULES[basePattern as keyof typeof SMART_INVALIDATION_RULES];
  
  if (invalidationPatterns) {
    // Smart invalidation - only clear related patterns
    apiCacheService.clearCacheByPatterns([...invalidationPatterns], storeId);
  }
}

// Cache-aware HTTP request function
function makeGetRequest<T>(url: string, config: any = {}): Promise<T> {
  const storeId = getCurrentStoreId();
  const fullUrl = appendMockMode(url);
  
  // Check cache first for cacheable URLs
  if (apiCacheService.isCacheable(url, 'GET')) {
    const cacheKey = apiCacheService.generateCacheKey(url, 'GET', storeId);
    const cachedEntry = apiCacheService.getCacheEntry(cacheKey);
    
    if (cachedEntry) {
      return Promise.resolve(cachedEntry.data as T);
    }
  }
  
  // Make actual network request
  return ax.get(fullUrl, config).then((res) => {
    // Cache successful responses
    if (apiCacheService.isCacheable(url, 'GET') && res.status >= 200 && res.status < 300) {
      const cacheKey = apiCacheService.generateCacheKey(url, 'GET', storeId);
      apiCacheService.setCacheEntry(cacheKey, res.data, url, 'GET', storeId);
    }
    
    return res.data as T;
  });
}

// Parse axios error body which may arrive as a raw string due to the
// transformResponse configured above.
//
// Exported for AS-10987: helpers/poReloadImages needed exactly this and had
// started a second copy. A duplicated parser drifting from this one is the same
// class of defect that ticket exists to remove, so callers share this instead.
export function parseErrorBody(data: unknown): any {
  if (data && typeof data === 'object') return data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

ax.interceptors.response.use((config) => {
  return config;
}, async (error) => {
  const originalRequest = error.config;
  // Lazy import: a top-level '@/router' import re-creates the module-init
  // cycle described at the top of this file. By the time a 401 lands here the
  // router module is long since loaded, so this resolves immediately.
  const pushHome = async () => {
    const { default: router } = await import('@/router');
    if (router.currentRoute.path !== '/') {
      router.push('/').catch((err: any) => {
        if (err?.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    }
  };
  // Backend signals a zombie store (app uninstalled but our record survived).
  // Redirect straight to Shopify OAuth so the createStore callback can clean
  // up the zombie record and issue fresh credentials. Avoid redirect loops by
  // only acting on the specific `code` contract.
  //
  // Use shopifyBridge.redirectExternal — inside the embedded iframe, direct
  // `window.top.location = url` fails cross-origin; App Bridge's CDN script
  // intercepts an anchor click with target="_top" and performs the redirect
  // via postMessage. Outside embed, it falls back to window.location.assign.
  if (error.response && error.response.status === 409) {
    const body = parseErrorBody(error.response.data);
    if (body && body.code === 'STORE_NEEDS_REINSTALL' && typeof body.install_url === 'string') {
      redirectExternal(body.install_url);
      return Promise.reject(error);
    }
  }
  // AS-11299: a 401 on the session exchange belongs to its caller
  // (shopifySessionAuth retries once with a fresh id_token, then falls back).
  // Evicting here would log the merchant out — and push them to `/` — in the
  // middle of a renewal that is about to succeed.
  if (error.response && error.response.status === 401
    && String(originalRequest?.url || '').startsWith('/shopify/session')) {
    return Promise.reject(error);
  }
  if (error.response && error.response.status === 401) {
    // AS-11619: fire-and-forget callers opt out of every recovery. A 401 on
    // tracking is dropped by its caller; it is never a reason to refresh,
    // renew, evict or navigate.
    if (originalRequest?.noAuthRetry) {
      return Promise.reject(error);
    }
    // AS-11619: this document has already been signed out. A straggler from
    // the burst that caused it gets its rejection and nothing else — no second
    // renewal, no second `pushHome`.
    if (sessionEvicted) {
      return Promise.reject(error);
    }
  }
  if (error.response && error.response.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;
    if (originalRequest.url === '/token/refresh') {
      await evictSession('refresh_token_rejected', pushHome);
      return Promise.reject(error);
    }
    try {
      const currentRefreshToken = localStorage.getItem('refresh_token');
      if (!currentRefreshToken) {
        // AS-11299: embedded OWNER sessions have no refresh token, but App
        // Bridge can always mint a fresh session token, so trade it for a new
        // JWT before evicting. (The exchange's own 401 returned above, so this
        // never re-enters the single-flight renewal from inside it.)
        const tokenUsed = String(originalRequest.headers?.Authorization || '').replace(/^Bearer /, '');
        const current = getIdentityToken();
        // A straggler from a burst that already renewed: the stored token is
        // newer than the one this request carried.
        const fresh = tokenUsed && current && current !== tokenUsed
          ? current
          : await renewShopifySession();
        if (fresh && fresh !== tokenUsed) {
          originalRequest.headers.Authorization = `Bearer ${fresh}`;
          return ax(originalRequest);
        }
        // AS-11619: inside the Shopify admin the login form is a dead end (an
        // embedded owner has no password), and `renewShopifySession` answers
        // null for a transient 5xx or timeout too — reload once first.
        if (isEmbbedApp()) {
          await reloadOnceOrEvict('session_renewal_failed', pushHome);
        } else {
          await evictSession('session_renewal_failed', pushHome);
        }
        return Promise.reject(error);
      }
      // Shared single-flight refresh: concurrent 401s here all await the same
      // request instead of each rotating the refresh token independently.
      // Storage writes happen in the refresher's onRefreshed callback.
      const token = await refreshAccessTokenOnce();
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return ax(originalRequest); // Retry the original request with the new access token.
    } catch (refreshError) {
      // AS-11619: reload once; a failure again inside the guard window evicts.
      await reloadOnceOrEvict('refresh_failed', pushHome);
      return Promise.reject(error);
    }
  }
  return Promise.reject(error);
});

/* tslint:disable-next-line */
function processData(data?: any) {
  if (data instanceof FormData) {
    // data.append('token', token)
  } else {
    // data.token = token
  }

  return data;
}

export function get<T>(url: string, data?: any, cancelToken: any = null): Promise<T> {
  const identity: string = getIdentityToken();
  if (isBlockedWhileSignedOut(url, identity)) {
    return Promise.reject(sessionEvictedError(url));
  }
  if (ax.defaults.cancelToken && cancelToken === null) {
    ax.defaults.cancelToken = undefined;
  }
  if (cancelToken) {
    ax.defaults.cancelToken = cancelToken;
  }
  
  const config = {
    params: processData(data),
    headers: {
      'Authorization': identity ? `Bearer ${identity}` : '',
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': '*',
    },
  };
  
  return makeGetRequest<any>(url, config)
    .then((res) => {
      if (typeof res === 'string') {
        return JSON.parse(res);
      } else {
        return res;
      }
    });
}

export function post<T>(url: string, data?: any, isForm: boolean = false, headers?: any): Promise<T> {
  const identity: string = getIdentityToken();
  if (isBlockedWhileSignedOut(url, identity)) {
    return Promise.reject(sessionEvictedError(url));
  }
  data = processData(data);
  let contentType = 'application/json';
  if (isForm) {
    // contentType = 'application/form-data';
    contentType = 'multipart/form-data';
  } else {
    data = JSON.stringify(data);
  }
  return ax
    .post(appendMockMode(url), data, {
      headers: {
        'Authorization': identity ? `Bearer ${identity}` : '',
        'Content-Type': contentType,
        ...headers,
      },
    })
    .then((res) => {
      // Handle cache invalidation after successful POST
      handleCacheInvalidation(url, 'POST', res.status);
      return JSON.parse(res.data);
    });
}

/**
 * AS-11619: fire-and-forget tracking (customer.io events). Sends nothing when
 * there is no identity — a signed-out page has nothing to attribute the event
 * to, and every such POST was a guaranteed 401 — and marks the request
 * `noAuthRetry`, so a 401 can never start a refresh, a renewal or an eviction.
 * Never rejects: tracking must not break its caller.
 */
export async function postTracking(url: string, data?: any): Promise<void> {
  const identity: string = getIdentityToken();
  if (!identity) {
    return;
  }
  // With an identity this never blocks; it is called for its side effect of
  // ending an eviction, like every other helper.
  if (isBlockedWhileSignedOut(url, identity)) {
    return;
  }
  try {
    await ax.post(appendMockMode(url), JSON.stringify(processData(data)), {
      noAuthRetry: true,
      headers: {
        'Authorization': `Bearer ${identity}`,
        'Content-Type': 'application/json',
      },
    });
  } catch (e) {
    // Fire-and-forget: the event is lost, the caller is not.
  }
}

export function post_with_config<T>(url: string, data?: any, options: AxiosRequestConfig = {}): Promise<T> {
  const identity: string = getIdentityToken();
  if (isBlockedWhileSignedOut(url, identity)) {
    return Promise.reject(sessionEvictedError(url));
  }
  let requestData = data;

  // Default headers that will apply if not overridden by options.headers
  // and if data is not FormData (which would have Axios set its own Content-Type)
  const defaultHeaders: Record<string, string> = {
    Authorization: identity ? `Bearer ${identity}` : '',
  };

  // If data is NOT FormData, we assume it should be JSON.
  // Axios will automatically set Content-Type for FormData.
  if (!(data instanceof FormData)) {
    requestData = JSON.stringify(data); // Ensure data is not undefined before stringifying
    defaultHeaders['Content-Type'] = 'application/json';
  }
  // For FormData, Axios handles the Content-Type including the boundary.
  // Do not manually set Content-Type to 'multipart/form-data' here without boundary.

  return ax
    .post(appendMockMode(url), requestData, {
      ...options, // Spread incoming options first
      headers: {
        ...defaultHeaders, // Apply defaults
        ...(options.headers || {}), // Then apply headers from options, allowing override
      },
    })
    .then((res) => {
      if (res.data && typeof res.data === 'string') {
        try {
          return JSON.parse(res.data);
        } catch (e) {
          // Depending on requirements, you might want to throw an error here
          // or return the raw string if that's a possible valid (non-JSON) response.
          // For now, returning raw string if parsing fails.
          return res.data as any;
        }
      }
      return res.data; // If already an object/other type
    })
    .catch((error) => {
        // Log the error with more context if available
        const errorDetails = error.response?.data || error.message || error.toString();
        if (error.response?.data) {
            // Throw an error that includes backend message if possible
            const backendError = new Error(error.response.data.message || 'Backend error during POST request');
            (backendError as any).response = error.response; // Attach full response for further handling
            throw backendError;
        }
        throw error; // Re-throw original error if no backend data
    });
}

export function put<T>(url: string, data?: any): Promise<T> {
  const identity: string = getIdentityToken();
  if (isBlockedWhileSignedOut(url, identity)) {
    return Promise.reject(sessionEvictedError(url));
  }
  data = processData(data);
  return ax
    .put(appendMockMode(url), JSON.stringify(data), {
      headers: {
        'Authorization': identity ? `Bearer ${identity}` : '',
        'Content-Type': 'application/json',
      },
    })
    .then((res) => {
      // Handle cache invalidation after successful PUT
      handleCacheInvalidation(url, 'PUT', res.status);
      if (res.data && typeof res.data === 'string') {
        try {
          return JSON.parse(res.data);
        } catch (e) {
          return res.data as any;
        }
      }
      return res.data;
    });
}

export function patch<T>(url: string, data?: any): Promise<T> {
  const identity: string = getIdentityToken();
  if (isBlockedWhileSignedOut(url, identity)) {
    return Promise.reject(sessionEvictedError(url));
  }
  data = processData(data);
  return ax
    .patch(appendMockMode(url), JSON.stringify(data), {
      headers: {
        'Authorization': identity ? `Bearer ${identity}` : '',
        'Content-Type': 'application/json',
      },
    })
    .then((res) => {
      // Handle cache invalidation after successful PATCH
      handleCacheInvalidation(url, 'PATCH', res.status);
      
      if (res.data && typeof res.data === 'string') {
        try {
          return JSON.parse(res.data);
        } catch (e) {
          // Depending on requirements, you might want to throw an error here
          // or return the raw string if that's a possible valid (non-JSON) response.
          // For now, returning raw string if parsing fails.
          return res.data as any;
        }
      }
      return res.data; // If already an object/other type
    });
}

export function del<T>(url: string, data?: any): Promise<T> {
  const identity: string = getIdentityToken();
  if (isBlockedWhileSignedOut(url, identity)) {
    return Promise.reject(sessionEvictedError(url));
  }
  data = processData(data);
  return ax
    .delete(appendMockMode(url), {
      data: JSON.stringify(data),
      headers: {
        Authorization: identity ? `Bearer ${identity}` : '',
      },
    })
    .then((res) => {
      // Handle cache invalidation after successful DELETE
      handleCacheInvalidation(url, 'DELETE', res.status);
      if (res.data && typeof res.data === 'string') {
        try {
          return JSON.parse(res.data);
        } catch (e) {
          return res.data as any;
        }
      }
      return res.data;
    });
}

export function download(url: string, data?: any): Promise<Blob> {
  const identity: string = getIdentityToken();
  if (isBlockedWhileSignedOut(url, identity)) {
    return Promise.reject(sessionEvictedError(url));
  }
  const requestConfig: AxiosRequestConfig = {
    responseType: 'blob', // Important
    headers: {
      Authorization: identity ? `Bearer ${identity}` : '',
    },
    params: data,
  };
  return ax.get(appendMockMode(url), requestConfig)
    .then((response) => {
      return new Blob([response.data]);
    });
}

/**
 * Pull a human-readable message out of an axios error.
 *
 * Needed because a request with `responseType: 'blob'` gets its error body
 * wrapped in a Blob too, so `error.response.data.message` is ALWAYS undefined
 * on a failed download — reading it directly silently discards whatever the
 * backend said and leaves the caller showing a generic failure string.
 *
 * @param error    the caught axios error
 * @param fallback message to use when nothing better can be recovered
 */
export async function extractApiErrorMessage(error: any, fallback: string): Promise<string> {
  if (error?.response?.data instanceof Blob) {
    try {
      const parsed = JSON.parse(await error.response.data.text());
      return parsed.message || parsed.error || fallback;
    } catch {
      // Not JSON (an HTML error page, a truncated body) — nothing to recover.
      return fallback;
    }
  }

  return error?.response?.data?.message || error?.message || fallback;
}

export async function getColorRules() {
  if (process.env.NODE_ENV === 'development') {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ data: mockColorRules });
      }, 100);
    });
  }

  return get('/api/color-rules');
}

// Cache management functions
export function clearApiCache() {
  apiCacheService.clearAllCache();
}

export function clearStoreCache(storeId?: string) {
  const targetStoreId = storeId || getCurrentStoreId();
  apiCacheService.clearStoreCache(targetStoreId);
}

export function getCacheStats() {
  return apiCacheService.getCacheStats();
}

// Export cache service for advanced usage
export { apiCacheService };
